import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as RestoreIcon } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { fetchArchivedProformaList, unArchiveProforma } from '../../slices/proforma';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

function ArchiveProformaList() {
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const [search, setSearch] = useState('');

  const dispatch = useDispatch();

  const debounceTimeout = useRef(null);

  const handleSearch = (e) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 100); // 300ms delay
  };


  useEffect(() => {
    dispatch(
      fetchArchivedProformaList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  const { data = [], totalRecord, status } = useSelector(state => state.proforma);

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));
  const handleRestore = (id) => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchiveProforma(id))
      .unwrap()
      .then(() => {
        message.success('Proforma Unarchived successfully!');
        dispatch(
          fetchArchivedProformaList({ page, pagesize, ...(search && { search: search }) }),
        );
      })
      .catch((err) => {
        console.error('Delete Error:', err); // Debugging the error
        message.error(`Error: ${err.message || 'Failed to delete the quotation.'}`);
      });
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
    },
    {
      title: 'Proforma Date',
      dataIndex: 'proforma_date',
      key: 'proforma_date',
      sorter: true,
      render: (proforma_date) => {
        return (
          <span>{proforma_date
            ? dayjs(proforma_date).format("DD-MM-YYYY")
            : "N/A"}</span>
        );
      },
    },
    {
      title: 'Proforma No.',
      dataIndex: 'proforma_no',
      key: 'proforma_no',
      sorter: true,
    },
    {
      title: 'Work Order No.',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      sorter: true,
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer',
      key: 'customer',
      sorter: true,
      render: (customer) => {
        return (
          customer
            ? customer.customer_name
            : "N/A"
        );
      },
    },
    {
      title: 'Freight Charges',
      dataIndex: 'freight_charges',
      key: 'freight_charges',
      sorter: true,
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: (record) => {
        return record ? (
          <span>{dayjs(record).format('DD-MM-YYYY HH:mm')}</span>
        ) : (
          <span>N/A</span>
        );
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'} </span>
        )
      }
    },
    {
      title: 'Action',
      dataIndex: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to Unarchive this record?"
              onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
              onCancel={() => message.info('Action cancelled')} // Optional cancel action
              okText="Yes"
              cancelText="No"
            >
              <RestoreIcon className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const handlePageChange = newPage => {
    setPage(newPage);
    setOrderBy('')
  };
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "SrNo" && sortOrder === "ascend") {
      sortingEnumKey = "SrNo";
    }
    if (sortField === "SrNo" && sortOrder === "descend") {
      sortingEnumKey = "-SrNo";
    }
    if (sortField === "workorder__order_date" && sortOrder === "ascend") {
      sortingEnumKey = "workorder__order_date";
    }
    if (sortField === "workorder__order_date" && sortOrder === "descend") {
      sortingEnumKey = "-workorder__order_date";
    }
    if (sortField === "workorder_no" && sortOrder === "ascend") {
      sortingEnumKey = "workorder_no";
    }
    if (sortField === "workorder_no" && sortOrder === "descend") {
      sortingEnumKey = "-workorder_no";
    }
    if (sortField === "customer" && sortOrder === "ascend") {
      sortingEnumKey = "customer__customer_name";
    }
    if (sortField === "customer" && sortOrder === "descend") {
      sortingEnumKey = "-customer__customer_name";
    }
    if (sortField === "packing_type" && sortOrder === "ascend") {
      sortingEnumKey = "packing_type";
    }
    if (sortField === "packing_type" && sortOrder === "descend") {
      sortingEnumKey = "-packing_type";
    }
    if (sortField === "proforma_date" && sortOrder === "ascend") {
      sortingEnumKey = "proforma_date";
    }
    if (sortField === "proforma_date" && sortOrder === "descend") {
      sortingEnumKey = "-proforma_date";
    }
    if (sortField === "freight_charges" && sortOrder === "ascend") {
      sortingEnumKey = "freight_charges";
    }
    if (sortField === "freight_charges" && sortOrder === "descend") {
      sortingEnumKey = "-freight_charges";
    }

    if (sortField === "advance_amount" && sortOrder === "descend") {
      sortingEnumKey = "-advance_amount";
    }
    if (sortField === "advance_amount" && sortOrder === "ascend") {
      sortingEnumKey = "advance_amount";
    }
    if (sortField === "delivery_schedule" && sortOrder === "descend") {
      sortingEnumKey = "-delivery_schedule";
    }
    if (sortField === "delivery_schedule" && sortOrder === "descend") {
      sortingEnumKey = "-delivery_schedule";
    }
    if (sortField === "weight_range" && sortOrder === "ascend") {
      sortingEnumKey = "weight_range";
    }
    if (sortField === "weight_range" && sortOrder === "descend") {
      sortingEnumKey = "-weight_range";
    }
    if (sortField === "proforma_no" && sortOrder === "ascend") {
      sortingEnumKey = "proforma_no";
    }
    if (sortField === "proforma_no" && sortOrder === "descend") {
      sortingEnumKey = "-proforma_no";
    }
    return sortingEnumKey;
  };
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  return (
    <>
      <div className='sticyaction'>
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2>Archive Proformas</h2>
            <div className="bredcrumbwrp">
              <Link to="/proforma-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: <Link to="/proforma-list">Proformas</Link> },
                  { title: 'Archive Proformas' }
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="middle">
              <Input
                placeholder="Search"
                className='searchbox'
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}
                style={{ width: '100%' }}
              />
              <Link>
                <Tooltip title="Filter">
                  <Button type="primary"
                    disabled={!search && !orderBy} className='iconlink'
                    onClick={resetFilter}
                  >
                    <FilterIcon />
                  </Button>                </Tooltip>
              </Link>


            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>

          <Table
            dataSource={dataSource}
            columns={columns}
            onChange={handleTableChange}
            pagination={false}
            className="mt-5"
            loading={status === 'loading'}
            scroll={{
              x: 880, // Horizontal Scroll
              y: 400,  // Vertical Scroll with Fixed Header
            }}
          />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
    </>
  );
}

export default ArchiveProformaList;
